/* eslint-disable react-refresh/only-export-components */
import {
  Button,
  TableCellLayout,
  TableColumnId,
  Tooltip,
} from "@fluentui/react-components";
import { Edit20Regular, Link16Regular } from "@fluentui/react-icons";
import { getDocumentIcon } from "@skanska/vsa-file-icon";
import { ReactNode } from "react";
import { Translation } from "react-i18next";
import ActivityIcon from "../assets/icons/icon-activity.svg";
import { DisplayField } from "../components/DisplayField";
import { IItemMetadata } from "../components/list/GenericDataGrid";
import LinkedDataGridCell from "../components/list/LinkedDataGridCell";
import TitleDataGridCell from "../components/list/TitleDataGridCell";
import ToolActivitiesDataGridCell from "../components/tool/ToolActivitiesDataGridCell";
import { Activity } from "../models/Activity";
import { Chapter } from "../models/Chapter";
import { ClientRoutes } from "../models/ClientRoutes";
import { FieldTypes } from "../models/FieldTypes";
import { Image } from "../models/Image";
import { Tool } from "../models/Tool";
import {
  compareDateField,
  compareNumberField,
  compareTextField,
} from "../services/FieldService";
import { getMediaFileTypeDisplay } from "../services/ImageService";
import { getToolFileName, sanitizeFileName } from "../services/ToolService";

// Interface created in place of the default TableColumnDefinition where the compare prop is a mandatory function which forces all columns to be sorta
export interface VsaTableColumnDefinition<TItem> {
  columnId: TableColumnId;
  compare: ((a: TItem, b: TItem) => number) | ""; // The check in Fluent if a column is sortable checks the length of this property, which is a typed function...
  renderHeaderCell: (data?: unknown) => ReactNode;
  renderCell: (item: TItem) => ReactNode;
}

export const CreatedFieldColumn: VsaTableColumnDefinition<IItemMetadata> = {
  columnId: "created",
  compare: (a, b) => compareDateField(a.createdTimeInUTC, b.createdTimeInUTC),
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("created")}</p>}</Translation>;
  },
  renderCell: (item) => {
    return (
      <TableCellLayout>
        <Translation>
          {(t) => (
            <DisplayField
              type={FieldTypes.Date}
              label={t("created")}
              value={item.createdTimeInUTC}
            />
          )}
        </Translation>
      </TableCellLayout>
    );
  },
};

export const CreatedByFieldColumn: VsaTableColumnDefinition<IItemMetadata> = {
  columnId: "createdBy",
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("createdBy")}</p>}</Translation>;
  },
  renderCell: (item) => (
    <TableCellLayout>
      <Translation>
        {(t) => (
          <DisplayField
            type={FieldTypes.User}
            label={t("createdBy")}
            value={item.createdBy}
          />
        )}
      </Translation>
    </TableCellLayout>
  ),
  compare: (a, b) => {
    return (a.createdBy?.displayName ?? "").localeCompare(
      b.createdBy?.displayName ?? ""
    );
  },
};

export const DraftCreatedByFieldColumn: VsaTableColumnDefinition<IItemMetadata> =
  {
    columnId: "draftCreatedBy",
    renderHeaderCell: () => {
      return (
        <Translation>
          {(t) => <p>{t("dataGridFieldLabels.draftCreatedBy")}</p>}
        </Translation>
      );
    },
    renderCell: (item) => (
      <TableCellLayout>
        <Translation>
          {(t) => (
            <DisplayField
              type={FieldTypes.User}
              label={t("dataGridFieldLabels.draftCreatedBy")}
              value={item.draftCreatedBy}
            />
          )}
        </Translation>
      </TableCellLayout>
    ),
    compare: (a, b) => {
      return (a.draftCreatedBy?.displayName ?? "").localeCompare(
        b.draftCreatedBy?.displayName ?? ""
      );
    },
  };

export const ModifiedFieldColumn: VsaTableColumnDefinition<IItemMetadata> = {
  columnId: "modified",
  compare: (a, b) => compareDateField(a.modifiedTimeInUTC, b.modifiedTimeInUTC),
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("modified")}</p>}</Translation>;
  },
  renderCell: (item) => {
    return (
      <TableCellLayout>
        <Translation>
          {(t) => (
            <DisplayField
              type={FieldTypes.Date}
              label={t("modified")}
              value={item.modifiedTimeInUTC}
            />
          )}
        </Translation>
      </TableCellLayout>
    );
  },
};

export const ModifiedByFieldColumn: VsaTableColumnDefinition<IItemMetadata> = {
  columnId: "modifiedBy",
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("modifiedBy")}</p>}</Translation>;
  },
  renderCell: (item) => {
    return (
      <TableCellLayout>
        <Translation>
          {(t) => (
            <DisplayField
              type={FieldTypes.User}
              label={t("modifiedBy")}
              value={item.modifiedBy}
            />
          )}
        </Translation>
      </TableCellLayout>
    );
  },
  compare: (a, b) => {
    return (a.createdBy?.displayName ?? "").localeCompare(
      b.createdBy?.displayName ?? ""
    );
  },
};

export const ActivityIdFieldColumn: VsaTableColumnDefinition<Activity> = {
  columnId: "activityId",
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("activityId")}</p>}</Translation>;
  },
  renderCell: (item) => {
    return (
      <TableCellLayout>
        <Translation>
          {(t) => (
            <DisplayField
              type={FieldTypes.Text}
              label={t("activityId")}
              value={item.activityId}
            />
          )}
        </Translation>
      </TableCellLayout>
    );
  },
  compare: (a, b) => compareTextField(a.activityId, b.activityId),
};

export const ToolIdFieldColumn: VsaTableColumnDefinition<Tool> = {
  columnId: "toolId",
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("toolId")}</p>}</Translation>;
  },
  renderCell: (item) => {
    return (
      <TableCellLayout>
        <Translation>
          {(t) => (
            <DisplayField
              type={FieldTypes.Text}
              label={t("toolId")}
              value={item.toolId}
            />
          )}
        </Translation>
      </TableCellLayout>
    );
  },
  compare: (a, b) => compareTextField(a.toolId, b.toolId),
};

export const VersionIdFieldColumn: VsaTableColumnDefinition<
  Activity | Tool | Image
> = {
  columnId: "versionId",
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("versionId")}</p>}</Translation>;
  },
  renderCell: (item) => {
    return (
      <TableCellLayout>
        <Translation>
          {(t) => (
            <DisplayField
              type={FieldTypes.Text}
              label={t("versionId")}
              value={item.versionId}
            />
          )}
        </Translation>
      </TableCellLayout>
    );
  },
  compare: (a, b) => compareTextField(a.versionId, b.versionId),
};

export const VersionFieldColumn: VsaTableColumnDefinition<
  Activity | Tool | Image
> = {
  columnId: "version",
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("version")}</p>}</Translation>;
  },
  renderCell: (item) => {
    return (
      <TableCellLayout>
        <Translation>
          {(t) => (
            <DisplayField
              type={FieldTypes.Number}
              label={t("version")}
              value={item.version}
            />
          )}
        </Translation>
      </TableCellLayout>
    );
  },
  compare: (a, b) => compareNumberField(a.version, b.version),
};

export const CommentsFieldColumn: VsaTableColumnDefinition<
  Activity | Tool | Image
> = {
  columnId: "comments",
  compare: "",
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("comments")}</p>}</Translation>;
  },
  renderCell: (activity) => {
    return (
      <TableCellLayout>
        <Translation>
          {(t) => (
            <DisplayField
              type={FieldTypes.Comments}
              label={t("comments")}
              value={activity.comments}
            />
          )}
        </Translation>
      </TableCellLayout>
    );
  },
};

export const PublishedByFieldColumn: VsaTableColumnDefinition<Activity | Tool> =
  {
    columnId: "publishedBy",
    renderHeaderCell: () => {
      return <Translation>{(t) => <p>{t("publishedBy")}</p>}</Translation>;
    },
    renderCell: (activity) => {
      return (
        <TableCellLayout>
          <Translation>
            {(t) => (
              <DisplayField
                type={FieldTypes.User}
                label={t("publishedBy")}
                value={activity.publishedBy}
              />
            )}
          </Translation>
        </TableCellLayout>
      );
    },
    compare: (a, b) =>
      compareTextField(a.publishedBy?.displayName, b.publishedBy?.displayName),
  };

export const PublishedDateFieldColumn: VsaTableColumnDefinition<
  Activity | Tool
> = {
  columnId: "publishedDate",
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("publishedDate")}</p>}</Translation>;
  },
  renderCell: (activity) => {
    return (
      <TableCellLayout>
        <Translation>
          {(t) => (
            <DisplayField
              type={FieldTypes.Date}
              label={t("publishedDate")}
              value={activity.publishedTimeInUTC}
            />
          )}
        </Translation>
      </TableCellLayout>
    );
  },
  compare: (a, b) =>
    compareDateField(a.publishedTimeInUTC, b.publishedTimeInUTC),
};

export const ToolActivitiesColumn: VsaTableColumnDefinition<Tool> = {
  columnId: "activities",
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("activities")}</p>}</Translation>
  ),
  renderCell: (tool) => <ToolActivitiesDataGridCell tool={tool} />,
  compare: "",
};

export const DocumentNameColumn: VsaTableColumnDefinition<Tool> = {
  columnId: "name",
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("name")}</p>}</Translation>
  ),
  renderCell: (tool) => (
    <TableCellLayout>
      <span style={{ wordBreak: "break-all" }}>
        {sanitizeFileName(tool.webUrl)}
      </span>
    </TableCellLayout>
  ),
  compare: (a, b) =>
    compareTextField(sanitizeFileName(a.webUrl), sanitizeFileName(b.webUrl)),
};

export const DocumentFileNameColumn: VsaTableColumnDefinition<Tool> = {
  columnId: "fileName",
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("name")}</p>}</Translation>
  ),
  renderCell: (tool) => (
    <TableCellLayout>
      <span style={{ wordBreak: "break-all" }}>{getToolFileName(tool)}</span>
    </TableCellLayout>
  ),
  compare: (a, b) => compareTextField(getToolFileName(a), getToolFileName(b)),
};

export const LinkUrlColumn: VsaTableColumnDefinition<Tool> = {
  columnId: "name",
  renderHeaderCell: () => <Translation>{(t) => <p>{t("url")}</p>}</Translation>,
  renderCell: (tool) => (
    <LinkedDataGridCell route={tool.webUrl} external>
      <span style={{ wordBreak: "break-all" }}>{tool.webUrl}</span>
    </LinkedDataGridCell>
  ),
  compare: (a, b) => compareTextField(a.webUrl, b.webUrl),
};

export const DraftAdminDisplayNameColumn: VsaTableColumnDefinition<Tool> = {
  columnId: "adminDisplayName",
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("adminDisplayNameLabel")}</p>}</Translation>
  ),
  renderCell: (tool) => (
    <LinkedDataGridCell route={`${ClientRoutes.ToolsDrafts}/${tool.versionId}`}>
      {tool.adminDisplayName}
    </LinkedDataGridCell>
  ),
  compare: (a, b) =>
    compareTextField(a.adminDisplayName ?? "", b.adminDisplayName ?? ""),
};

export const PublishedAdminDisplayNameColumn: VsaTableColumnDefinition<Tool> = {
  columnId: "adminDisplayName",
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("adminDisplayNameLabel")}</p>}</Translation>
  ),
  renderCell: (tool) => (
    <LinkedDataGridCell
      route={`${ClientRoutes.ToolsPublished}/${tool.versionId}`}
    >
      {tool.adminDisplayName}
    </LinkedDataGridCell>
  ),
  compare: (a, b) =>
    compareTextField(a.adminDisplayName ?? "", b.adminDisplayName ?? ""),
};

// Icon / Type columns
export const ActivityIconColumn: VsaTableColumnDefinition<Activity> = {
  columnId: "type",
  compare: "",
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("type")}</p>}</Translation>
  ),
  renderCell: () => (
    <Translation>
      {(t) => (
        <TableCellLayout>
          <Tooltip content={t("activity")} relationship="label">
            <img
              src={ActivityIcon}
              alt="type"
              width={16}
              height={16}
              style={{ alignSelf: "start" }}
            />
          </Tooltip>
        </TableCellLayout>
      )}
    </Translation>
  ),
};

export const ToolFileIconColumn: VsaTableColumnDefinition<Tool> = {
  columnId: "type",
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("type")}</p>}</Translation>
  ),
  renderCell: (tool) => (
    <TableCellLayout>
      <Translation>
        {(t) => (
          <>
            <Tooltip
              content={tool.fileUniqueId ? t("document") : t("link")}
              relationship="label"
            >
              {tool.fileType ? (
                <img
                  src={getDocumentIcon(tool.fileType)}
                  alt="type"
                  width={16}
                  height={16}
                  style={{ alignSelf: "start" }}
                />
              ) : (
                <Link16Regular />
              )}
            </Tooltip>
          </>
        )}
      </Translation>
    </TableCellLayout>
  ),
  compare: (a, b) => {
    const aIcon = getDocumentIcon((a as Tool).fileType);
    const bIcon = getDocumentIcon((b as Tool).fileType);
    return aIcon.localeCompare(bIcon);
  },
};

// Title columns
export const DraftActivityTitleColumn: VsaTableColumnDefinition<IItemMetadata> =
  {
    columnId: "title",
    compare: (a, b) => compareTextField(a.title, b.title),
    renderHeaderCell: () => (
      <Translation>
        {(t) => <p>{t("dataGridFieldLabels.activityTitle")}</p>}
      </Translation>
    ),
    renderCell: (item) => (
      <TitleDataGridCell
        item={item}
        route={ClientRoutes.Activities + `/${item.versionId}`}
      />
    ),
  };

export const PublishedActivityTitleColumn: VsaTableColumnDefinition<IItemMetadata> =
  {
    columnId: "title",
    compare: (a, b) => compareTextField(a.title, b.title),
    renderHeaderCell: () => (
      <Translation>
        {(t) => <p>{t("dataGridFieldLabels.activityTitle")}</p>}
      </Translation>
    ),
    renderCell: (item) => (
      <TitleDataGridCell
        item={item}
        route={`${ClientRoutes.ActivitiesPublished}/${item.versionId}`}
      />
    ),
  };

export const DraftToolTitleColumn: VsaTableColumnDefinition<IItemMetadata> = {
  columnId: "title",
  compare: (a, b) => compareTextField(a.title, b.title),
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("title")}</p>}</Translation>
  ),
  renderCell: (item) => (
    <TitleDataGridCell
      item={item}
      route={`${ClientRoutes.ToolsDrafts}/${item.versionId}`}
    />
  ),
};

export const PublishedToolTitleColumn: VsaTableColumnDefinition<IItemMetadata> =
  {
    columnId: "title",
    compare: (a, b) => compareTextField(a.title, b.title),
    renderHeaderCell: () => (
      <Translation>{(t) => <p>{t("title")}</p>}</Translation>
    ),
    renderCell: (item) => (
      <TitleDataGridCell
        item={item}
        route={`${ClientRoutes.ToolsPublished}/${item.versionId}`}
      />
    ),
  };

export const ChapterTitleColumn: VsaTableColumnDefinition<Chapter> = {
  columnId: "title",
  compare: (a, b) => compareTextField(a.title, b.title),
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("title")}</p>}</Translation>
  ),
  renderCell: (item) => (
    <TitleDataGridCell
      item={item}
      route={`${ClientRoutes.Chapters}/${item.id}`}
    />
  ),
};

// Chapters column

export const ChaptersColumn: VsaTableColumnDefinition<Activity | Tool | Image> =
  {
    columnId: "chapters",
    compare: "",
    renderHeaderCell: () => {
      return <Translation>{(t) => <p>{t("chapters")}</p>}</Translation>;
    },
    renderCell: (item) => {
      return (
        <TableCellLayout>
          <Translation>
            {(t) => (
              <DisplayField
                type={FieldTypes.Tag}
                label={t("chapters")}
                value={item.chapters}
              />
            )}
          </Translation>
        </TableCellLayout>
      );
    },
  };

export const ImageIdFieldColumn: VsaTableColumnDefinition<Image> = {
  columnId: "imageId",
  compare: (a, b) => compareTextField(a.imageId, b.imageId),
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("imageId")}</p>}</Translation>;
  },
  renderCell: (item) => {
    return <TableCellLayout>{item.imageId || item.versionId}</TableCellLayout>;
  },
};

export const ImageFileNameColumn: VsaTableColumnDefinition<Image> = {
  columnId: "fileName",
  compare: (a, b) => compareTextField(a.fileName, b.fileName),
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("fileName")}</p>}</Translation>;
  },
  renderCell: (item) => {
    return (
      <TableCellLayout>
        <span style={{ wordBreak: "break-all" }}>{item.fileName}</span>
      </TableCellLayout>
    );
  },
};

export const ChapterImageTitleColumn: VsaTableColumnDefinition<Image> = {
  columnId: "title",
  compare: (a, b) => compareTextField(a.title, b.title),
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("title")}</p>}</Translation>
  ),
  renderCell: (item) => (
    <TitleDataGridCell
      item={item}
      route={`${ClientRoutes.MediaChapterEdit}/${item.imageId}`}
    />
  ),
};

export const DraftActivityImageTitleColumn: VsaTableColumnDefinition<Image> = {
  columnId: "title",
  compare: (a, b) => compareTextField(a.title, b.title),
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("title")}</p>}</Translation>
  ),
  renderCell: (item) => (
    <TitleDataGridCell
      item={item}
      route={`${ClientRoutes.MediaActivityEdit}/${item.versionId}`}
    />
  ),
};

export const ActivityImageTitleColumn: VsaTableColumnDefinition<Image> = {
  columnId: "title",
  compare: (a, b) => compareTextField(a.title, b.title),
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("title")}</p>}</Translation>
  ),
  renderCell: (item) => (
    <TitleDataGridCell
      item={item}
      route={`${ClientRoutes.MediaActivityEdit}/overview/${item.versionId}`}
    />
  ),
};

export const ImageCaptionColumn: VsaTableColumnDefinition<Image> = {
  columnId: "imageCaption",
  compare: "",
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("imageCaption")}</p>}</Translation>
  ),
  renderCell: (item) => (
    <TableCellLayout>
      <Translation>
        {(t) => (
          <DisplayField
            type={FieldTypes.Text}
            label={t("imageCaption")}
            value={item.title}
          />
        )}
      </Translation>
    </TableCellLayout>
  ),
};

export const CommentColumn: VsaTableColumnDefinition<Image | Tool> = {
  columnId: "comments",
  compare: "",
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("commentLabel")}</p>}</Translation>
  ),
  renderCell: (item) => (
    <TableCellLayout>
      <Translation>
        {(t) => (
          <DisplayField
            type={FieldTypes.Comments}
            label={t("commentLabel")}
            value={item.comments}
          />
        )}
      </Translation>
    </TableCellLayout>
  ),
};

export const ImageTypeColumn: VsaTableColumnDefinition<Image> = {
  columnId: "fileType",
  compare: (a, b) =>
    compareTextField(getMediaFileTypeDisplay(a), getMediaFileTypeDisplay(b)),
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("type")}</p>}</Translation>;
  },
  renderCell: (item) => {
    const displayType = getMediaFileTypeDisplay(item);

    return <TableCellLayout>{displayType}</TableCellLayout>;
  },
};

export const ImageFileSizeColumn: VsaTableColumnDefinition<Image> = {
  columnId: "fileSizeBytes",
  compare: (a, b) => compareNumberField(a.fileSizeBytes, b.fileSizeBytes),
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("fileSize")}</p>}</Translation>;
  },
  renderCell: (item) => {
    return <TableCellLayout>{item.fileSizeBytes}</TableCellLayout>;
  },
};

export const ImageDimensionsColumn: VsaTableColumnDefinition<Image> = {
  columnId: "pixelDimensions",
  compare: (a, b) => {
    const aSize =
      (a.pixelDimensions?.width || 0) * (a.pixelDimensions?.height || 0);
    const bSize =
      (b.pixelDimensions?.width || 0) * (b.pixelDimensions?.height || 0);
    return compareNumberField(aSize, bSize);
  },
  renderHeaderCell: () => {
    return <Translation>{(t) => <p>{t("imageDimensions")}</p>}</Translation>;
  },
  renderCell: (item) => {
    if (item.pixelDimensions?.width && item.pixelDimensions?.height) {
      return (
        <TableCellLayout>
          {item.pixelDimensions.width} × {item.pixelDimensions.height}
        </TableCellLayout>
      );
    }
    return <TableCellLayout>-</TableCellLayout>;
  },
};

export const ImageNameColumn: VsaTableColumnDefinition<Image> = {
  columnId: "name",
  compare: (a, b) => compareTextField(a.fileName, b.fileName),
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("name")}</p>}</Translation>
  ),
  renderCell: (item) => {
    return (
      <TableCellLayout>
        <Translation>
          {(t) => (
            <span style={{ wordBreak: "break-all" }}>
              <DisplayField
                type={FieldTypes.Text}
                label={t("imageName")}
                value={item.fileName || ""}
              />
            </span>
          )}
        </Translation>
      </TableCellLayout>
    );
  },
};

export const ImageActionsColumn = (
  onEdit: (imageId: string) => void
): VsaTableColumnDefinition<Image> => ({
  columnId: "actions",
  compare: () => 0,
  renderHeaderCell: () => (
    <Translation>{(t) => <p>{t("actions")}</p>}</Translation>
  ),
  renderCell: (item) => {
    const imageId = item.imageId || item.versionId;
    return (
      <TableCellLayout>
        <Button
          appearance="subtle"
          icon={<Edit20Regular />}
          onClick={() => onEdit(imageId)}
          title={item.title || imageId}
        />
      </TableCellLayout>
    );
  },
});
